"use client";

import { type RefObject, useEffect, useRef } from "react";
import { type RealtimeMessageEvent, useVisitorRealtime } from "@/lib/use-visitor-realtime";
import { type ConversationSinks, routeConversationEvent } from "./open-conversation";

interface Opts {
  /** From `openConversationChatId`; `null` closes the stream. */
  chatId: string | null;
  token: string | null;
  visitorId?: string | null;
  enabled: boolean;
  aiName: string;
  /** The mounted MessagesTab's handler. */
  handlerRef: RefObject<((e: RealtimeMessageEvent) => void) | null>;
  sinks: Omit<ConversationSinks, "forward">;
  /** Refetch the open conversation (messages + assignment) after a gap. */
  resync: () => void;
}

/**
 * The tab's one per-chat stream: the open conversation only. After a gap it resyncs: a mid-thread drop
 * (`onReopen`), or the panel being closed and reopened on the same thread, where MessagesTab stayed
 * mounted and would otherwise keep serving what it had.
 */
export function useOpenConversationStream({
  chatId,
  token,
  visitorId,
  enabled,
  aiName,
  handlerRef,
  sinks,
  resync,
}: Opts): void {
  const latest = useRef({ aiName, sinks, resync });
  latest.current = { aiName, sinks, resync };

  useVisitorRealtime({
    chatId,
    token,
    visitorId,
    enabled,
    onMessage: (event) =>
      routeConversationEvent(
        event,
        { ...latest.current.sinks, forward: (e) => handlerRef.current?.(e) },
        latest.current.aiName,
      ),
    onReopen: () => latest.current.resync(),
  });

  const lastChatIdRef = useRef<string | null>(null);
  const previousRef = useRef<string | null>(null);
  useEffect(() => {
    if (chatId && previousRef.current === null && lastChatIdRef.current === chatId) {
      latest.current.resync();
    }
    if (chatId) lastChatIdRef.current = chatId;
    previousRef.current = chatId;
  }, [chatId]);
}
